import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Col, Container, FormControl, InputGroup, Row, Image, Form } from 'react-bootstrap';
import Navbar from 'react-bootstrap/Navbar';
import { isEmpty, difference, filter, union } from 'lodash';
import moment from 'moment';

import time from '../../utils/time';
import MultiselectFilter from '../../components/MultiselectFilter';
import DateFilter from '../../components/DateFilter';
import { fetchUsername } from '../../actions/auth';
import {
  fetchGlobalAdminStatsDat,
  fetchGlobalAdminStatsJson,
  getSubscriptionData,
  fetchBillableInfo,
  fetchRepoInfo,
  fetchPriceInfo,
} from '../../actions/globalAdminStats';
import {
  getImportDataAll,
  getImportDataAllWithTimePeriod,
  getImportDataForSubscriptionInfo,
} from '../../actions/trt';
import { clientsExcelSelector } from '../../reducers/trt/trtReducer';
import { applyFilters, convertBytesToGigabytes, convertDate } from '../../utils/common';
import auth from '../../utils/auth';
import AUTH_LOGOUT_URL from '../../constants/auth';
import {PROJECT_STATS_URL} from '../../constants/urls';
import ClientsTable from '../../components/ClientsTable';
import {
  globalAdminStatisticsClientAdminsSelector,
  globalAdminStatisticsClientDataSelector,
  globalAdminStatisticsSelector,
  globalAdminStatisticsWithTimePeriodSelector,
  getSubscriptionServices,
  subscriptionInfoSelector,
} from '../../reducers/globalAdminStats/globalAdminStatsReducer';
import AdminStatisticsScreen from '../AdminStatisticsScreen';
import GlobalResources from '../GlobalResources';
import sort from '../../utils/sort';
import excel from '../../utils/excel';
import trtStatistics from '../../utils/trtStatistics';

import './index.css';

const BILLABLE_FILTER = {
  ALL: 'all',
  BILLABLE: 'billable',
  NOT_BILLABLE: 'not-billable',
}

const REPO_FILTER = {
  ALL: 'all',
  HAS_REPO: 'has-repo',
  HAS_CLIENT: 'has-client',
  HAS_REPO_AND_NO_CLIENT: 'has-repo-and-no-client',
  HAS_NO_REPO: 'has-no-repo',
  HAS_NO_CLIENT: 'has-no-client',
  HAS_NO_REPO_AND_NO_CLIENT: 'has-no-repo-and-no-client',
}

const GlobalAdminStatsScreen = () => {
  const [ t ] = useTranslation();

  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [showResources, setShowResources] = useState(false);
  const [sortOrder, setSortOrder] = useState("ascending");
  const [statisticsSelection, setStatisticsSelection] = useState([]);
  const [globalAdminStatsDate, setGlobalAdminStatsDate] = useState({startDate: '', endDate: ''});
  const [selectedClients, setSelectedClients] = useState([]);
  const [subscriptionInfoDate, setSubscriptionInfoDate] = useState({startDate: '', endDate: ''});
  const [billableFilter, setBillableFilter] = useState(BILLABLE_FILTER.ALL);
  const [repoFilter, setRepoFilter] = useState(REPO_FILTER.ALL);
  const [trtDate, setTrtDate] = useState({startDate: '', endDate: ''});

  const globalAdminStatsJson = useSelector(state => state.globalAdminStats.globalAdminStatsJson);
  const username = useSelector(state => state.auth.username);
  const trtClients = useSelector(state => state.trt.clients);
  const trtClientsForSubscriptionInfo = useSelector(state => state.trt.clientsForSubscriptionInfo);
  const trtExcelData = useSelector(clientsExcelSelector);
  const subscriptionInfo = useSelector(subscriptionInfoSelector);

  const billableInfo = useSelector(state => state.globalAdminStats.billableInfo);
  const repoInfo = useSelector(state => state.globalAdminStats.repoInfo);
  const priceInfo = useSelector(state => state.globalAdminStats.priceInfo);

  const subscriptionServices = useSelector(getSubscriptionServices);

  const searchFilter = data => {
    const searchTerm = inputValue.toLowerCase();
    return data.filter(client => {
      return (!isEmpty(client.name) && client.name.toLowerCase().indexOf(searchTerm) !== -1);
    });
  };

  const changeSortOrder = () => {
    setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
  };

  const selectFilter = useCallback(data => (
    data.filter(client => (selectedClients.find(selectedProject => client.name === selectedProject.name)))
  ), [selectedClients]);

  const sortMethods = {
    name: sort.sortByName,
    allUsers: sort.sortByInt('allUsers'),
    activeUsers: sort.sortByInt('activeUsers'),
    usersWithAtLeastOneCommit: sort.sortByInt('usersWithAtLeastOneCommit'),
    datetimeOfFirstCommit: sort.sortByDate('datetimeOfFirstCommit'),
    datetimeOfSecondCommit: sort.sortByDate('datetimeOfSecondCommit'),
    datetimeOfLastCommit: sort.sortByDate('datetimeOfLastCommit'),
    averageCommitsPerUser: sort.sortByFloat('averageCommitsPerUser'),
    averageCommitsPerMonth: sort.sortByFloat('averageCommitsPerMonth'),
    numberOfCommits: sort.sortByInt('numberOfCommits'),
    numberOfProjects: sort.sortByInt('numberOfProjects'),
    workPapersDiskUsage: sort.sortByInt('workPapersDiskUsage'),
    svnFolderDiskUsage: sort.sortByInt('svnFolderDiskUsage'),
    svnDbFolderDiskUsage: sort.sortByInt('svnDbFolderDiskUsage'),
    repositoryDiskUsage: sort.sortByInt('repositoryDiskUsage'),
    htmlDiskUsage: sort.sortByInt('htmlDiskUsage'),
  };

  const [sortMethod, setSortMethod] = useState(() => sort.sortByName);
  const [sortMethodName, setSortMethodName] = useState(() => sort.sortByName.name);

  const removeFilters = useCallback(() => {
    setInputValue("");
    setSelectedClients([]);
    setGlobalAdminStatsDate({startDate: '', endDate: ''});
    setSortMethod(() => sort.sortByName);
    setSortMethodName(() => sort.sortByName.name);
    setSortOrder("ascending");
    setBillableFilter(BILLABLE_FILTER.ALL);
    setRepoFilter(REPO_FILTER.ALL);
  }, [
    setInputValue,
    setSelectedClients,
    setGlobalAdminStatsDate,
    setSortMethod,
    setSortMethodName,
    setSortOrder,
    setBillableFilter,
    setRepoFilter,
  ]);

  const billableTypeFilter = useCallback(data => {
    return data.filter((client) => {
      if (billableFilter === BILLABLE_FILTER.ALL || isEmpty(billableInfo)) {
        return true;
      }
      if (billableFilter === BILLABLE_FILTER.BILLABLE) {
        return billableInfo.some(
          billableClient => billableClient.repo_name === client.name,
        );
      }
      if (billableFilter === BILLABLE_FILTER.NOT_BILLABLE) {
        return !billableInfo.some(
          billableClient => billableClient.repo_name === client.name
        );
      }

      return true;
    });
  }, [billableFilter, billableInfo]);

  const repoInfoFilter = useCallback((data) => {
    return data.filter((client) => {
      if (repoFilter === REPO_FILTER.ALL || isEmpty(repoInfo)) {
        return true;
      }
      if (repoFilter === REPO_FILTER.HAS_REPO) {
        return repoInfo.repos.some(
          repoName => repoName === client.name,
        );
      }
      if (repoFilter === REPO_FILTER.HAS_CLIENT) {
        return repoInfo.repos_with_clients.some(
          repoName => repoName === client.name,
        );
      }
      if (repoFilter === REPO_FILTER.HAS_NO_REPO) {
        return !repoInfo.repos.some(
          repoName => repoName === client.name,
        );
      }
      if (repoFilter === REPO_FILTER.HAS_NO_CLIENT) {
        return !repoInfo.repos_with_clients.some(
          repoName => repoName === client.name,
        );
      }
      if (repoFilter === REPO_FILTER.HAS_REPO_AND_NO_CLIENT) {
        const hasRepo = repoInfo.repos.some(
          repoName => repoName === client.name,
        );

        const noClient = !repoInfo.repos_with_clients.some(
          repoName => repoName === client.name,
        );

        return hasRepo && noClient;
      }
      if (repoFilter === REPO_FILTER.HAS_NO_REPO_AND_NO_CLIENT) {
        const noRepo = !repoInfo.repos.some(
          repoName => repoName === client.name,
        );

        const noClient = !repoInfo.repos_with_clients.some(
          repoName => repoName === client.name,
        );

        return noRepo && noClient;
      }

      return true;
    });
  }, [repoFilter, repoInfo]);

  const filterFunctions = [
    clients => searchFilter(clients),
    clients => billableTypeFilter(clients),
    clients => repoInfoFilter(clients),
  ];

   // statistics
  const originalClients = useSelector(globalAdminStatisticsSelector);
  const clientData = useSelector(globalAdminStatisticsClientDataSelector);
  const clientAdmins = useSelector(globalAdminStatisticsClientAdminsSelector);

  // filtered projects
  const clients = useMemo(() => applyFilters(
      originalClients,
      filterFunctions,
      sortMethod,
      sortOrder
  ), [
      originalClients,
      filterFunctions,
      sortMethod,
      sortOrder]
  );

  const clientsForStatistics = useMemo(() => selectFilter(clients), [clients, selectFilter]);
  const globalAdminStatisticsTimePeriod =
     useSelector(globalAdminStatisticsWithTimePeriodSelector(
         clientsForStatistics,
         globalAdminStatsDate.startDate,
         globalAdminStatsDate.endDate
     ));

  const selectClient = client => {
    setSelectedClients(prevSelectedClients => union(prevSelectedClients, [client]));
  };

  const deselectClient = client => {
    const newSelectedClients = filter(selectedClients, selectedClient => selectedClient.name !== client.name);
    setSelectedClients(newSelectedClients);
  };

  const selectAllClients = () => {
    setSelectedClients(prevSelectedClients => union(prevSelectedClients, clients));
  };

  const deselectAllClients = () => {
    setSelectedClients(prevSelectedClients => difference(prevSelectedClients, clientsForStatistics));
  };

  // choose statistics object
  const STATISTICS = useMemo(() => ({
   general: t('selectStatistics.general'),
   clientInfo: t('selectStatistics.clientInfo.title'),
   subscriptionInfo: t('selectStatistics.subscriptionInfo.title'),
   trt: t('selectStatistics.trt'),
  }), [t]);

  // table headers
  const headers = [
    t('adminStatisticsTable.headers.name'),
    t('adminStatisticsTable.headers.allUsers'),
    t('adminStatisticsTable.headers.activeUsers'),
    t('adminStatisticsTable.headers.usersWithAtLeastOneCommit'),
    t('adminStatisticsTable.headers.datetimeOfFirstCommit'),
    t('adminStatisticsTable.headers.datetimeOfSecondCommit'),
    t('adminStatisticsTable.headers.datetimeOfLastCommit'),
    t('adminStatisticsTable.headers.averageCommitsPerUser'),
    t('adminStatisticsTable.headers.averageCommitsPerMonth'),
    t('adminStatisticsTable.headers.numberOfCommits'),
    t('adminStatisticsTable.headers.numberOfProjects'),
    t('adminStatisticsTable.headers.workPapersDiskUsage'),
    t('adminStatisticsTable.headers.svnFolderDiskUsage'),
    t('adminStatisticsTable.headers.svnDbFolderDiskUsage'),
    t('adminStatisticsTable.headers.repositoryDiskUsage'),
    t('adminStatisticsTable.headers.htmlDiskUsage'),
  ];

  useEffect(() => {
    dispatch(fetchGlobalAdminStatsDat());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchGlobalAdminStatsJson());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBillableInfo());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRepoInfo());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchPriceInfo());
  }, [dispatch]);

   useEffect(() => {
    dispatch(fetchUsername());
  }, [dispatch]);

   useEffect(() => {
     const { startDate, endDate } = subscriptionInfoDate;
     if (startDate && endDate) {
       dispatch(getSubscriptionData(
        moment(startDate).format('YYYY-MM-DD'),
        moment(endDate).format('YYYY-MM-DD'),
       ));

      dispatch(getImportDataForSubscriptionInfo(
        moment(startDate).format('YYYY-MM-DD'),
        moment(endDate).format('YYYY-MM-DD'),
      ));
     }
  }, [dispatch, subscriptionInfoDate]);

   useEffect(() => {
     const { startDate, endDate } = trtDate;

     if (startDate && endDate) {
       dispatch(getImportDataAllWithTimePeriod(
           moment(startDate).format('YYYY-MM-DD'),
           moment(endDate).format('YYYY-MM-DD'),
       ))
     }

     if (!startDate && !endDate) {
       dispatch(getImportDataAll());
     }
  }, [dispatch, trtDate]);

  const formatClientsToExcel = clientsToFormat => {
    return clientsToFormat.map(client => {
      const averageCommitsPerUser = parseFloat(client.averageCommitsPerUser);
      const averageCommitsPerMonth = parseFloat(client.averageCommitsPerMonth);
      const workPapersDiskUsageGB = convertBytesToGigabytes(client.workPapersDiskUsage, 100);
      const svnFolderDiskUsageGB = convertBytesToGigabytes(client.svnFolderDiskUsage, 100);
      const svnDbFolderDiskUsageGB = convertBytesToGigabytes(client.svnDbFolderDiskUsage, 100);
      const repositoryDiskUsageGB = convertBytesToGigabytes(client.repositoryDiskUsage, 100);
      const htmlDiskUsageGB = convertBytesToGigabytes(client.htmlDiskUsage, 100);
      return {
        ...client,
        averageCommitsPerUser,
        averageCommitsPerMonth,
        workPapersDiskUsageGB,
        svnFolderDiskUsageGB,
        svnDbFolderDiskUsageGB,
        repositoryDiskUsageGB,
        htmlDiskUsageGB,
      };
    });
  };

  const trtInfoFileName = translationKey => `${t(translationKey)}-${time.convertDatetimeToLocal(new Date())}`;
  const trtInfoFileNameWithTimePeriod = (translationKey, startDate, endDate) =>
      `${t(translationKey)}-${moment(startDate).format('YYYY-MM-DD')} ${moment(endDate).format('YYYY-MM-DD')}`;

  return (
    <div>
      <Navbar >
        <Navbar.Brand href="#"><Image src="favicon.png" className="navbar-brand-icon" rounded />{t('navbar.brand')} {global.appVersion}</Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-end global-stats-navigation">
            <Row>
              <Col md="5">
                <GlobalResources
                  show={showResources}
                  setShow={setShowResources}
                />
              </Col>
              <Col md="5">
                <Button
                    variant="outline-dark"
                    href={PROJECT_STATS_URL}>
                  {t('navbar.projectStatistics')}
                </Button>
              </Col>
              <Col md="2">
                <Button variant="outline-dark" href={AUTH_LOGOUT_URL}>{`${t('navbar.logout')}`}</Button>
              </Col>
           </Row>
          </Navbar.Collapse>
      </Navbar>
      <Container align="center">
        <hr />
        <Container align="left">
          <Row>
            <Col md="1"><span>{t("common.client")}:</span></Col>
            <Col><h5>{auth.getUsernameAndClient(username).client}</h5></Col>
          </Row>
          <Row>
            <Col md="1"><span>{t("common.user")}:</span></Col>
            <Col><h5>{auth.getUsernameAndClient(username).name}</h5></Col>
          </Row>
        </Container>
        <hr />
        <InputGroup className="mb-3">
          <FormControl
            onChange={e => setInputValue(e.target.value)}
            value={inputValue}
            placeholder={t('filters.clientSearchPlaceholder')}
            aria-label="Filter"
            aria-describedby="basic-addon1"
            />
        </InputGroup>
        <Row className="mb-3 justify-content-center">
          <Col md={6}>
            <Button className="filtersFlagAllButton" onClick={removeFilters} variant="outline-info">{t('filters.removeFilters')}</Button>
          </Col>
        </Row>
        <Row className="mb-3 justify-content-center">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Billable Filter</Form.Label>
              <Form.Control
                as="select"
                value={billableFilter}
                onChange={(e) => setBillableFilter(e.target.value)}
              >
                <option value={BILLABLE_FILTER.ALL}>All</option>
                <option value={BILLABLE_FILTER.BILLABLE}>Billable</option>
                <option value={BILLABLE_FILTER.NOT_BILLABLE}>Not Billable</option>
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3 justify-content-center">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Repo Filter</Form.Label>
              <Form.Control
                as="select"
                value={repoFilter}
                onChange={(e) => setRepoFilter(e.target.value)}
              >
                <option value={REPO_FILTER.ALL}>All</option>
                <option value={REPO_FILTER.HAS_REPO}>Has Repo</option>
                <option value={REPO_FILTER.HAS_CLIENT}>Has Client</option>
                <option value={REPO_FILTER.HAS_REPO_AND_NO_CLIENT}>Has Repo and No Client</option>
                <option value={REPO_FILTER.HAS_NO_REPO}>Has No Repo</option>
                <option value={REPO_FILTER.HAS_NO_CLIENT}>Has No Client</option>
                <option value={REPO_FILTER.HAS_NO_REPO_AND_NO_CLIENT}>Has No Repo and No Client</option>
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
      </Container>
      <div className="clients-table clients-table-projects">
        <h2>{t('globalAdminStatsTable.title')}</h2>
        <ClientsTable
            headers={headers}
            rows={clients}
            changeSort={changeSortOrder}
            sort={sortOrder}
            selectedItems={selectedClients}
            selectItem={selectClient}
            deselectItem={deselectClient}
            selectAllItems={selectAllClients}
            deselectAllItems={deselectAllClients}
            sortMethod={sortMethod}
            sortMethodName={sortMethodName}
            sortMethods={sortMethods}
            setSortMethod={setSortMethod}
            setSortMethodName={setSortMethodName}
        />
      </div>
      <hr/>
      <Row className="justify-content-center mb-3">
        <MultiselectFilter
          name={t('selectStatistics.dropdownTitle')}
          dropdownData={Object.values(STATISTICS)}
          multiselectedData={statisticsSelection}
          setMultiselectedData={setStatisticsSelection}
          buttonVariant='secondary'
        />
      </Row>
     {statisticsSelection.includes(STATISTICS.general) ?
          <>
          <Row className="users-and-rights-container">
            <h3>{STATISTICS.general}</h3>
          </Row>
          <Row className="users-and-rights-container">
            <Row className="users-and-rights">
              <DateFilter
                  name={t('selectStatistics.selectDate')}
                  selectedDate={globalAdminStatsDate}
                  setSelectedDate={setGlobalAdminStatsDate}
                  buttonVariant='outline'
              />
            </Row>
          </Row></> :
          <></>
      }
      {
        (statisticsSelection.includes(STATISTICS.general) && globalAdminStatsDate.startDate && globalAdminStatsDate.endDate ?
          <Row className="justify-content-center">
            <Col md={6}>
              <br/>
              <h4 className="statistics-date">
                {`Date: ${convertDate(globalAdminStatsDate.startDate)} - ${convertDate(globalAdminStatsDate.endDate)}`}
              </h4>
            </Col>
          </Row> :
          <></>
        )
      }
      {
        statisticsSelection.includes(STATISTICS.general) &&
        <div className="current-steps-statistics-table">
          <AdminStatisticsScreen
              showStats={false}
              showStatsWithTimePeriod
              statsWithTimePeriod={globalAdminStatisticsTimePeriod}
          />
        </div>
      }
      {
        statisticsSelection.includes(STATISTICS.clientInfo) &&
        <div>
          <Row className="justify-content-center mt-3">
            <Button
                variant="outline-dark"
                onClick={() => excel.exportToExcel(
                    formatClientsToExcel(clients),
                    `${t('selectStatistics.clientInfo.clientInfoFromTableExcelFileName')}-${
                      time.convertDatetimeToLocal(globalAdminStatsJson.last_update)
                    }`
                )}
            >
              {t('selectStatistics.clientInfo.downloadExcelClientInfoFromTable')}
            </Button>
          </Row>
          <Row className="justify-content-center mt-3">
            <Button
                variant="outline-dark"
                onClick={() => excel.exportToExcel(
                    clientData,
                    `${t('selectStatistics.clientInfo.clientInfoExcelFileName')}-${
                      time.convertDatetimeToLocal(globalAdminStatsJson.last_update)
                    }`
                )}
            >
              {t('selectStatistics.clientInfo.downloadExcelClientInfo')}
            </Button>
          </Row>
          <Row className="justify-content-center mt-3">
            <Button
                variant="outline-dark"
                 onClick={() => excel.exportToExcel(
                    clientAdmins,
                    `${t('selectStatistics.clientInfo.clientAdminsExcelFileName')}-${
                      time.convertDatetimeToLocal(globalAdminStatsJson.last_update)
                    }`
                )}
            >
              {t('selectStatistics.clientInfo.downloadClientAdminInfo')}
            </Button>
          </Row>
        </div>
      }
      {
        statisticsSelection.includes(STATISTICS.subscriptionInfo) && (
            <div>
              <Row className="users-and-rights-container mt-3">
                <h3>{STATISTICS.subscriptionInfo}</h3>
              </Row>
              <Row className="users-and-rights-container mb-5">
                <Row className="users-and-rights">
                  <DateFilter
                      name={t('selectStatistics.selectDate')}
                      selectedDate={subscriptionInfoDate}
                      setSelectedDate={setSubscriptionInfoDate}
                      buttonVariant='outline'
                  />
                </Row>
              </Row>
              {
                (statisticsSelection.includes(STATISTICS.subscriptionInfo) && subscriptionInfoDate.startDate && subscriptionInfoDate.endDate ?
                    <Row className="justify-content-center">
                      <Col md={6}>
                        <br/>
                        <h4 className="statistics-date">
                          {`Date: ${convertDate(subscriptionInfoDate.startDate)} - ${convertDate(subscriptionInfoDate.endDate)}`}
                        </h4>
                      </Col>
                    </Row> : <></>
                )
              }
              <Row className="justify-content-center mt-3 mb-5">
                <Button
                    variant="outline-dark"
                    disabled={!(subscriptionInfoDate.startDate && subscriptionInfoDate.endDate) || (isEmpty(clientsForStatistics))}
                    onClick={() => {
                      excel.exportToExcelGetSubscriptionsInfo(
                          clientsForStatistics,
                          subscriptionInfoDate,
                          subscriptionInfo,
                          subscriptionServices,
                          t('selectStatistics.subscriptionInfo.invoicingExcelFileName'),
                      )
                    }}
                >
                  {t('selectStatistics.subscriptionInfo.downloadExcelInvoicing')}
                </Button>
              </Row>
              <Row className="justify-content-center mt-3 mb-5">
                <Button
                    variant="outline-dark"
                    disabled={!(subscriptionInfoDate.startDate && subscriptionInfoDate.endDate) || (isEmpty(clientsForStatistics))}
                    onClick={() => {
                      excel.exportToExcelGetSubscriptionsInfoPerUser(
                          clientsForStatistics,
                          clientData,
                          trtClientsForSubscriptionInfo,
                          billableInfo,
                          priceInfo,
                          subscriptionInfoDate,
                          subscriptionInfo,
                          subscriptionServices,
                          t('selectStatistics.subscriptionInfo.invoicingExcelFileNamePerUser'),
                      )
                    }}
                >
                  {t('selectStatistics.subscriptionInfo.downloadExcelInvoicingPerUser')}
                </Button>
              </Row>
            </div>
        )
      }
      {
        statisticsSelection.includes(STATISTICS.trt) && (
            <div>
              <Row className="users-and-rights-container mt-3">
                <h3>{STATISTICS.trt}</h3>
              </Row>
              <Row className="users-and-rights-container mb-5">
                <Row className="users-and-rights">
                  <DateFilter
                      name={t('selectStatistics.selectDate')}
                      selectedDate={trtDate}
                      setSelectedDate={setTrtDate}
                      buttonVariant='outline'
                  />
                </Row>
              </Row>
              {
                (statisticsSelection.includes(STATISTICS.trt) && trtDate.startDate && trtDate.endDate ?
                        <Row className="justify-content-center">
                          <Col md={6}>
                            <br/>
                            <h4 className="statistics-date">
                              {`Date: ${convertDate(trtDate.startDate)} - ${convertDate(trtDate.endDate)}`}
                            </h4>
                          </Col>
                        </Row> : <></>
                )
              }
              <Row className="justify-content-center mt-3 mb-5">
                <Button
                    variant="outline-dark"
                    onClick={() => {
                      const hasTrtDate = trtDate.startDate && trtDate.endDate;
                      const fileName =
                          hasTrtDate ?
                              trtInfoFileNameWithTimePeriod(
                                  'selectStatistics.trtInfo.importsInfoExcelFileNameAll',
                                  trtDate.startDate,
                                  trtDate.endDate
                              )
                              : trtInfoFileName('selectStatistics.trtInfo.importsInfoExcelFileNameAll');
                      excel.exportToExcel(
                          trtExcelData,
                          fileName,
                      )
                    }}
                >
                  {t('selectStatistics.trtInfo.downloadExcelImportsInfoAll')}
                </Button>
              </Row>
              <Row className="justify-content-center mt-3 mb-5">
                <Button
                    variant="outline-dark"
                    disabled={isEmpty(clientsForStatistics)}
                    onClick={() => {
                      const hasTrtDate = trtDate.startDate && trtDate.endDate;
                      const fileName = hasTrtDate ?
                          trtInfoFileNameWithTimePeriod(
                              'selectStatistics.trtInfo.importsInfoUsersExcelFileNameAll',
                              trtDate.startDate,
                              trtDate.endDate
                          )
                          : trtInfoFileName('selectStatistics.trtInfo.importsInfoUsersExcelFileNameAll');
                      const { clientNames, usersData } = trtStatistics.getClientsWithUsersExcelStatistics(
                          trtClients,
                          clientsForStatistics,
                      );
                      if (isEmpty(usersData)) {
                        return;
                      }
                      excel.exportToExcelWithSheets(
                          usersData,
                          clientNames,
                          fileName,
                      )
                    }}
                >
                  {t('selectStatistics.trtInfo.downloadExcelImportsInfoUsersAll')}
                </Button>
              </Row>
            </div>
        )
      }
      <hr/>
      <Container>
        <Row className="justify-content-md-center">
          <Col className="lastUpdateInfoCol col-md-6 col-md-offset-3">
            {t('footer.lastUpdated')}: {time.convertDatetimeToLocal(globalAdminStatsJson.last_update)}
          </Col>
        </Row>
      </Container>
   </div>
  );
};

export default GlobalAdminStatsScreen;
